"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function TheHabitHero() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imgRef,
    isVisible: isImgVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="236928" id="prescriptions-delivered-with-medical-precision" className="relative min-h-[90vh] flex items-center overflow-hidden bg-background text-foreground">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--color-primary)_0%,_transparent_120%)] opacity-5 pointer-events-none" />

      <div className="container mx-auto px-6 md:px-12 relative z-10 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          {}
          <div ref={textRef} className={`flex flex-col items-start gap-10 transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <h1 className="font-default text-6xl md:text-7xl lg:text-[84px] leading-[1.05] text-foreground tracking-tighter max-w-2xl font-bold">
              Prescriptions<br />
              <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">Delivered.</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-muted-foreground font-default max-w-xl">
              Integrated with Atlanta’s top doctor network for swift, secure, and professional home delivery.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <Button asChild size="lg" className="h-14 px-8 text-lg rounded-lg bg-primary text-primary-foreground shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 font-default">
                <Link to="#">Start Delivery</Link>
              </Button>
              <Button asChild variant="ghost" size="lg" className="h-14 px-8 text-lg rounded-lg border border-border hover:bg-accent transition-all duration-300 font-default">
                <Link to="/CareCyclePage">How it Works</Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={imgRef} className={`relative flex justify-center items-center transition-all duration-1000 delay-300 ease-out ${isImgVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}>
            <style>{`
              @keyframes float-subtle {
                0%, 100% { transform: translateY(0px); }
                50% { transform: translateY(-24px); }
              }
              .animate-float-subtle {
                animation: float-subtle 7s ease-in-out infinite;
              }
            `}</style>
            
            <div className="relative w-full max-w-[500px] aspect-square animate-float-subtle">
              {}
              <div className="absolute inset-0 bg-primary/10 blur-[120px] rounded-lg transform scale-90" />
              
              {}
              <div className="absolute -bottom-4 -right-4 z-20 bg-white/70 backdrop-blur-md border border-white/20 p-6 rounded-xl shadow-2xl max-w-[240px]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-3 h-3 rounded-lg bg-secondary animate-pulse" />
                  <span className="text-xs font-mono font-bold uppercase tracking-widest text-foreground">Real-Time Tracking</span>
                </div>
                <div className="h-2 w-full bg-muted rounded-lg overflow-hidden">
                  <div className="h-full bg-secondary w-3/4 rounded-lg" />
                </div>
                <p className="text-[10px] mt-2 font-mono text-muted-foreground">ETA: 14 MINS • BUCKHEAD, GA</p>
              </div>

              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1651003/img/premium_medicine_bottle_atlanta.png" alt="GOTMEDS.IO Premium Prescription Packaging" className="relative z-10 w-full h-full object-contain drop-shadow-4xl" />
            </div>
          </div>

        </div>
      </div>
    </section>;
}